import React from 'react';
import { Box, styled } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { EqualisationFeesFormValues } from './FormWrapper';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import {
  EqualisationSection,
  FeesEqualisationTreatment,
} from 'further-types/lp/equalisation';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import EqualisationPage from '../EqualisationPage';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import BasicSelect from 'further-ui/components/BasicSelect';
import { FeesEqualisationTreatmentLabels } from 'further-ui/labels';
import AvatarWithName from 'components/new/AvatarWithName';
import NumberInput from 'further-ui/components/NumberInput';
import { sumBy } from 'lodash';
import useEqualiseFees from '../../hooks/useEqualiseFees';
import FeeCardRow from './FeeCardRow';

const FeeCardWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

interface EqualiseFeesContentProps {
  lpFeeTemplates: Array<{
    id: string;
    name: string;
  }>;
  baseCurrency: CurrencyCode;
}

const EqualiseFeesContent: React.FC<EqualiseFeesContentProps> = ({
  lpFeeTemplates,
}) => {
  const { getValues, control, watch, setValue } =
    useFormContext<EqualisationFeesFormValues>();
  const {
    previousSection,
    nextSection,
    currentSection,
    goToNextSection,
    goToListPage,
    goToPreviousSection,
    baseCurrency,
  } = useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();
  const { recalculateFromTreatmentChange, recalculateFromPercentageChange } =
    useEqualiseFees();

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getEqualisationFees,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
      feeTemplateOverrides: [],
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const { fields: totalCommitmentFees } = useFieldArray({
    control,
    name: 'totalCommitmentFees',
  });

  const watchedFees = watch('totalCommitmentFees');

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        totalCommitmentFees: getValues('totalCommitmentFees').map((fee) => ({
          ...fee,
          lpFeeTemplateId:
            fee.lpFeeTemplateId !== 'none' ? fee.lpFeeTemplateId : undefined,
        })),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handleSaveAsDraft = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection,
        totalCommitmentFees: getValues('totalCommitmentFees').map((fee) => ({
          ...fee,
          lpFeeTemplateId:
            fee.lpFeeTemplateId !== 'none' ? fee.lpFeeTemplateId : undefined,
        })),
      },
      {
        onSuccess: () => {
          goToListPage();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const hasPartialTreatmentRow = watchedFees?.some(
    (fee) =>
      fee.feesEqualisationTreatment === FeesEqualisationTreatment.Partial,
  );

  const columns: Array<TableColumn<(typeof totalCommitmentFees)[number]>> = [
    {
      label: 'Investor name',
      key: 'investorName',
      width: 250,
      render: (fee) => (
        <AvatarWithName name={fee.investorName ?? ''} avatarSize="lg" />
      ),
    },
    {
      label: 'Status',
      key: 'status',
      width: 150,
      render: (fee) => fee.investorStatus,
    },
    {
      label: 'Fee template',
      key: 'lpFeeTemplateId',
      width: 230,
      render: (_, rowIndex) => (
        <Controller
          control={control}
          name={`totalCommitmentFees.${rowIndex}.lpFeeTemplateId`}
          render={({ field }) => (
            <BasicSelect
              value={field.value}
              size="small"
              fullWidth
              onChange={(value) => {
                const row = watchedFees?.[rowIndex];
                if (value === 'none') {
                  setValue(`totalCommitmentFees.${rowIndex}`, {
                    ...row,
                    lpFeeTemplateId: 'none',
                    lpFees: [],
                    totalEqualisationFeeIncTaxLcy: 0,
                    feesEqualisationTreatment: FeesEqualisationTreatment.None,
                    feesEqualisationTreatmentPercentage: 0,
                  });
                  return;
                }
                const newLpFees = row?.lpFeesByTemplate?.[value]?.lpFees || [];
                const newTotal = sumBy(
                  newLpFees,
                  (fee: { equalisationFeeIncTaxLcy: number }) =>
                    fee.equalisationFeeIncTaxLcy || 0,
                );

                const updatedValue = {
                  ...row,
                  lpFeeTemplateId: value,
                  lpFees: newLpFees,
                  totalEqualisationFeeIncTaxLcy: newTotal,
                  feesEqualisationTreatment:
                    row.lpFeesByTemplate?.[value]?.feesEqualisationTreatment,
                  feesEqualisationTreatmentPercentage:
                    row.lpFeesByTemplate?.[value]
                      ?.feesEqualisationTreatmentPercentage,
                };
                setValue(`totalCommitmentFees.${rowIndex}`, updatedValue);
              }}
              options={[
                { value: 'none', label: 'None' },
                ...lpFeeTemplates.map((template) => ({
                  value: template.id,
                  label: template.name,
                })),
              ]}
            />
          )}
        />
      ),
    },
    {
      label: 'Management fee equalisation treatment',
      key: 'equalisationTreatment',
      width: 240,
      render: (_, rowIndex) => (
        <Controller
          control={control}
          name={`totalCommitmentFees.${rowIndex}.feesEqualisationTreatment`}
          render={({ field }) => (
            <BasicSelect
              value={field.value}
              size="small"
              fullWidth
              onChange={(value) => {
                const row = watchedFees?.[rowIndex];
                const recalculatedValues = recalculateFromTreatmentChange(
                  row,
                  value,
                  row.lpFeeTemplateId,
                );
                if (recalculatedValues)
                  setValue(
                    `totalCommitmentFees.${rowIndex}`,
                    recalculatedValues,
                  );
                field.onChange(value);
              }}
              options={Object.entries(FeesEqualisationTreatmentLabels).map(
                ([optionValue, label]) => ({
                  value: optionValue as FeesEqualisationTreatment,
                  label,
                }),
              )}
            />
          )}
        />
      ),
    },
    ...(hasPartialTreatmentRow
      ? [
          {
            label: '',
            key: 'feesEqualisationTreatmentPercentage',
            width: 110,
            render: (_, rowIndex) => {
              return watchedFees?.[rowIndex]?.feesEqualisationTreatment ===
                FeesEqualisationTreatment.Partial ? (
                <Controller
                  control={control}
                  name={`totalCommitmentFees.${rowIndex}.feesEqualisationTreatmentPercentage`}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        showPercentSuffix
                        value={field.value}
                        min={0}
                        max={100}
                        onChange={(percentage) => {
                          const row = watchedFees?.[rowIndex];
                          const recalculated = recalculateFromPercentageChange(
                            row,
                            percentage ?? 0,
                          );
                          if (recalculated)
                            setValue(`totalCommitmentFees.${rowIndex}`, {
                              ...recalculated,
                              feesEqualisationTreatmentPercentage:
                                percentage ?? 0,
                            });
                        }}
                      />
                    );
                  }}
                />
              ) : null;
            },
          },
        ]
      : []),
    {
      label: 'Fees outstanding',
      key: 'feesOutstanding',
      width: 160,
      render: (_, rowIndex) =>
        numberToCurrencyString(
          watchedFees?.[rowIndex]?.totalEqualisationFeeIncTaxLcy,
          {
            currency: baseCurrency,
          },
        ),
    },
  ];

  return (
    <EqualisationPage
      actionBar={
        <EqualisationFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleSaveAsDraft={handleSaveAsDraft}
          loading={updateEqualisation.isPending}
        />
      }
      subheading="Review new commitment-based management fees."
    >
      <Table
        isExpandedRow={(row) => expandedRows.has(row.id)}
        isExpandableRow={() => true}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        scrollable
        getExpandedContent={(row, _, rowIndex) => (
          <FeeCardWrapper>
            <FeeCardRow
              index={rowIndex}
              value={watchedFees?.[rowIndex] ?? row}
              baseCurrency={baseCurrency}
              onChange={(newValue) =>
                setValue(`totalCommitmentFees.${rowIndex}`, newValue)
              }
            />
          </FeeCardWrapper>
        )}
        rows={totalCommitmentFees}
        columns={columns}
      />
    </EqualisationPage>
  );
};

export default EqualiseFeesContent;
